import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from './Layout';
import {
    Box, CircularProgress, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
    Autocomplete, Popover, IconButton, Badge, Button,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import InvoiceService from '../services/InvoiceService';
import CustomerService from '../services/CustomerService';
import ProjectService from '../services/ProjectService';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { glassSurface } from '../styles/glass';

const ACCENT = '#427898';

const STATUSES = [
    { code: 'DRAFT',          color: '#94a3b8', label: 'Draft' },
    { code: 'ISSUED',         color: '#3b82f6', label: 'Issued' },
    { code: 'SENT',           color: '#6366f1', label: 'Sent' },
    { code: 'PARTIALLY_PAID', color: '#f59e0b', label: 'Partially Paid' },
    { code: 'PAID',           color: '#10b981', label: 'Paid' },
    { code: 'OVERDUE',        color: '#ef4444', label: 'Overdue' },
    { code: 'CANCELLED',      color: '#cbd5e1', label: 'Cancelled' },
    { code: 'WRITTEN_OFF',    color: '#64748b', label: 'Written Off' },
];

const statusColor = (code) => STATUSES.find(s => s.code === code)?.color ?? '#94a3b8';
const statusLabel = (code) => STATUSES.find(s => s.code === code)?.label ?? code;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('de-CH') : '—';
const fmtCHF = (v) => v != null ? `CHF ${Number(v).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

function FilterPill({ label, active, onClick, color }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                px: 1.5, py: 0.5, borderRadius: '20px', cursor: 'pointer',
                fontSize: 13, fontWeight: active ? 700 : 500,
                bgcolor: active ? (color || ACCENT) : 'transparent',
                color: active ? '#fff' : '#64748B',
                border: '1.5px solid',
                borderColor: active ? (color || ACCENT) : '#E2E8F0',
                userSelect: 'none', whiteSpace: 'nowrap',
                transition: 'all 0.12s',
                '&:hover': { borderColor: color || ACCENT, color: active ? '#fff' : (color || ACCENT), bgcolor: active ? (color || ACCENT) : `${color || ACCENT}10` },
            }}
        >
            {label}
        </Box>
    );
}

function StatusPill({ code }) {
    const color = statusColor(code);
    const label = statusLabel(code);
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color }}>{label}</Typography>
        </Box>
    );
}

function ActiveChip({ label, onRemove }) {
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.4, borderRadius: '16px', bgcolor: '#EEF2F6', border: '1px solid #CBD5E1', fontSize: 12, color: '#334155', fontWeight: 500 }}>
            {label}
            <Box component="span" onClick={onRemove} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#94A3B8', ml: 0.25, '&:hover': { color: '#475569' } }}>
                <CloseIcon sx={{ fontSize: 13 }} />
            </Box>
        </Box>
    );
}

export default function InvoicesPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();

    const agentFilters = location.state?.agentFilters || {};

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const [selectedStatuses, setSelectedStatuses] = useState(agentFilters.status ? [agentFilters.status] : []);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);

    const [filterAnchor, setFilterAnchor] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [projects, setProjects] = useState([]);

    const fetchTimer = useRef(null);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const filters = {
                statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
                customerId: selectedCustomer?.id ?? undefined,
                projectId: selectedProject?.id ?? undefined,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
            };
            const data = await InvoiceService.search(page, rowsPerPage, filters);
            setRows(data.content ?? []);
            setTotal(data.totalElements ?? 0);
        } catch {
            showToast('Failed to load invoices', 'error');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, selectedStatuses, selectedCustomer, selectedProject, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        clearTimeout(fetchTimer.current);
        fetchTimer.current = setTimeout(fetchRows, 200);
        return () => clearTimeout(fetchTimer.current);
    }, [fetchRows]);

    useEffect(() => {
        if (location.state?.agentFilters) {
            window.history.replaceState({}, '');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        CustomerService.getCustomers(0, 200).then(d => {
            const list = d.content ?? [];
            setCustomers(list);
            if (agentFilters.customerName) {
                const match = list.find(c => c.name === agentFilters.customerName);
                if (match) setSelectedCustomer(match);
            }
        }).catch(() => {});
        ProjectService.getAllProjectsFlat().then(setProjects).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleStatus = (code) => {
        setPage(0);
        setSelectedStatuses(prev => prev.includes(code) ? prev.filter(s => s !== code) : [...prev, code]);
    };

    const clearFilters = () => {
        setSelectedStatuses([]);
        setDateFrom('');
        setDateTo('');
        setSelectedCustomer(null);
        setSelectedProject(null);
        setPage(0);
    };

    const advancedFilterCount = [selectedCustomer, selectedProject, dateFrom, dateTo].filter(Boolean).length;
    const hasActiveChips = advancedFilterCount > 0;
    const hasAnyFilter = selectedStatuses.length > 0 || advancedFilterCount > 0;

    return (
        <Layout>
            <Box sx={{ p: 3 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5 }}>
                    <ReceiptIcon sx={{ color: ACCENT, fontSize: 28 }} />
                    <Typography variant="h5" fontWeight={700} color="#0F172A">Invoices</Typography>
                    {total > 0 && (
                        <Box sx={{ px: 1.5, py: 0.25, borderRadius: '8px', bgcolor: '#EEF4F8', color: ACCENT, fontSize: 13, fontWeight: 700 }}>
                            {total}
                        </Box>
                    )}
                    <Button
                        size="small"
                        onClick={() => navigate('/invoice-style')}
                        startIcon={<PaletteOutlinedIcon sx={{ fontSize: 15 }} />}
                        sx={{
                            ml: 'auto', textTransform: 'none', fontSize: 12, fontWeight: 600,
                            color: '#64748b',
                            border: '1px solid', borderColor: '#e2e8f0',
                            borderRadius: '8px', px: 1.25, py: 0.4,
                            '&:hover': { bgcolor: '#EEF4F8', borderColor: ACCENT, color: ACCENT },
                        }}
                    >
                        PDF Style
                    </Button>
                </Box>

                {/* Filter bar */}
                <Box sx={{ mb: hasActiveChips ? 1.5 : 3 }}>
                    <Box sx={{ ...glassSurface, display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, overflow: 'hidden' }}>
                        {/* Status pills */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'auto', flex: 1, pb: 0.25, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                            <FilterPill label="All" active={selectedStatuses.length === 0} onClick={() => { setSelectedStatuses([]); setPage(0); }} />
                            {STATUSES.map(s => (
                                <FilterPill key={s.code} label={s.label} active={selectedStatuses.includes(s.code)} onClick={() => toggleStatus(s.code)} color={s.color} />
                            ))}
                        </Box>

                        {/* Advanced filters button */}
                        <Box sx={{ flexShrink: 0, pl: 1, borderLeft: '1px solid #E2E8F0', ml: 0.5 }}>
                            <Badge badgeContent={advancedFilterCount} color="primary"
                                sx={{ '& .MuiBadge-badge': { bgcolor: ACCENT, fontSize: 10, minWidth: 16, height: 16 } }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={e => setFilterAnchor(e.currentTarget)}
                                    sx={{ color: advancedFilterCount > 0 ? ACCENT : '#64748B', border: '1px solid', borderColor: advancedFilterCount > 0 ? ACCENT : '#E2E8F0', borderRadius: '8px', p: 0.75 }}
                                >
                                    <TuneIcon fontSize="small" />
                                </IconButton>
                            </Badge>
                        </Box>
                    </Box>

                    {/* Active filter chips */}
                    {hasActiveChips && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.25, px: 0.5 }}>
                            {selectedCustomer && <ActiveChip label={`Customer: ${selectedCustomer.name}`} onRemove={() => { setSelectedCustomer(null); setPage(0); }} />}
                            {selectedProject && <ActiveChip label={`Project: ${selectedProject.name}`} onRemove={() => { setSelectedProject(null); setPage(0); }} />}
                            {dateFrom && <ActiveChip label={`From: ${dateFrom}`} onRemove={() => { setDateFrom(''); setPage(0); }} />}
                            {dateTo && <ActiveChip label={`To: ${dateTo}`} onRemove={() => { setDateTo(''); setPage(0); }} />}
                            <Box onClick={clearFilters} sx={{ display: 'inline-flex', alignItems: 'center', fontSize: 12, color: '#94A3B8', cursor: 'pointer', fontWeight: 500, '&:hover': { color: '#475569' } }}>
                                Clear all
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Advanced filters popover */}
                <Popover
                    open={Boolean(filterAnchor)}
                    anchorEl={filterAnchor}
                    onClose={() => setFilterAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{ sx: { mt: 1, borderRadius: '12px', boxShadow: '0 8px 32px rgba(15,23,42,0.12)', border: '1px solid #E2E8F0', width: 320, p: 2.5 } }}
                >
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A', mb: 2 }}>Advanced Filters</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Autocomplete
                            size="small"
                            options={customers}
                            getOptionLabel={c => c.name ?? ''}
                            value={selectedCustomer}
                            onChange={(_, v) => { setSelectedCustomer(v); setPage(0); }}
                            renderInput={params => <TextField {...params} label="Customer" />}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                        />
                        <Autocomplete
                            size="small"
                            options={projects}
                            getOptionLabel={p => p.name ?? ''}
                            value={selectedProject}
                            onChange={(_, v) => { setSelectedProject(v); setPage(0); }}
                            renderInput={params => <TextField {...params} label="Project" />}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                        />
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                            <TextField
                                size="small" type="date" label="From"
                                value={dateFrom}
                                onChange={e => { setDateFrom(e.target.value); setPage(0); }}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                size="small" type="date" label="To"
                                value={dateTo}
                                onChange={e => { setDateTo(e.target.value); setPage(0); }}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>
                    </Box>
                    {advancedFilterCount > 0 && (
                        <Box onClick={() => { clearFilters(); setFilterAnchor(null); }} sx={{ mt: 2, fontSize: 13, color: '#94A3B8', cursor: 'pointer', textAlign: 'center', '&:hover': { color: '#475569' } }}>
                            Clear all filters
                        </Box>
                    )}
                </Popover>

                {/* Table */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                        <CircularProgress sx={{ color: ACCENT }} />
                    </Box>
                ) : rows.length === 0 ? (
                    <Box sx={{ ...glassSurface, py: 8, textAlign: 'center' }}>
                        <ReceiptIcon sx={{ fontSize: 40, color: '#CBD5E1', mb: 1.5 }} />
                        <Typography fontWeight={600} color="#475569" mb={0.5}>No invoices found</Typography>
                        <Typography fontSize={13} color="#94A3B8">
                            {hasAnyFilter ? 'Try adjusting your filters' : 'Invoices will appear here once created'}
                        </Typography>
                    </Box>
                ) : (
                    <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                        {['Invoice #', 'Customer', 'Project', 'Date', 'Due Date', 'Amount', 'Status', ''].map((h, i) => (
                                            <TableCell key={i} sx={{ fontWeight: 700, fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.6, py: 1.5, ...(i === 7 ? { width: 36, p: 0 } : {}) }}>
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map(row => (
                                        <TableRow
                                            key={row.id}
                                            onClick={() => navigate(`/invoices/${row.id}`)}
                                            sx={{ height: 52, cursor: 'pointer', '&:last-child td': { border: 0 }, '&:hover': { bgcolor: '#F8FAFC' }, '&:hover .row-chevron': { opacity: 1 } }}
                                        >
                                            <TableCell sx={{ fontWeight: 700, color: '#0F172A', fontSize: 13 }}>
                                                {row.displayName ?? `#${row.id}`}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13, color: '#475569' }}>{row.customerName ?? '—'}</TableCell>
                                            <TableCell sx={{ fontSize: 13, color: '#475569' }}>{row.projectName ?? '—'}</TableCell>
                                            <TableCell sx={{ fontSize: 13, color: '#64748B' }}>{fmtDate(row.invoiceDate)}</TableCell>
                                            <TableCell sx={{ fontSize: 13, color: row.status === 'OVERDUE' ? '#ef4444' : '#64748B', fontWeight: row.status === 'OVERDUE' ? 700 : 400 }}>
                                                {fmtDate(row.dueDate)}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{fmtCHF(row.totalPrice)}</TableCell>
                                            <TableCell><StatusPill code={row.status} /></TableCell>
                                            <TableCell sx={{ p: 0, pr: 1 }}>
                                                <ChevronRightIcon className="row-chevron" sx={{ fontSize: 18, color: '#94A3B8', opacity: 0, transition: 'opacity 0.15s' }} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                            rowsPerPageOptions={[10, 20, 50]}
                            sx={{ borderTop: '1px solid #F1F5F9' }}
                        />
                    </Paper>
                )}
            </Box>
        </Layout>
    );
}
